export const ROUTES = {
  ROOT: '/',
  LOGIN: '/login',
  /**
   * Phase 12.3F (G-6) — where an invited company owner sets their first
   * password. Public: the visitor has no credential yet, which is the point.
   * The URL carries only a non-secret invitation identifier.
   */
  COMPANY_OWNER_ACTIVATION: '/activate-company-owner',
  CHANGE_PASSWORD: '/change-password',
  /**
   * Where a tenant user lands when the backend has refused this workspace —
   * subscription SUSPENDED / EXPIRED / CANCELLED, or the tenant account itself
   * not ACTIVE. Public: the page shows nothing tenant-specific and must render
   * whether or not a session survives. It carries a `?reason=` param for copy
   * selection only; backend enforcement is unaffected by it.
   */
  ACCESS_RESTRICTED: '/access-restricted',
  CUSTOMERS: '/customers',
  CUSTOMER_DETAIL: '/customers/:id',
  CUSTOMERS_RECOVERY: '/customers/recovery-bin',
  EQUIPMENT: '/equipment',
  EQUIPMENT_DETAIL: '/equipment/:id',
  EQUIPMENT_SCAN: '/scan/:token',
  MANUFACTURERS: '/master-data/manufacturers',
  EQUIPMENT_CATEGORIES: '/master-data/equipment-categories',
  EQUIPMENT_TYPES: '/master-data/equipment-types',
  EQUIPMENT_MODELS: '/master-data/equipment-models',
  ACCESSORIES: '/master-data/accessories',
  SERVICE_TASKS: '/master-data/service-tasks',
  INSPECTION_PARAMETERS: '/master-data/inspection-parameters',
  SERVICE_TYPES: '/master-data/service-types',
  WARRANTY_TYPES: '/master-data/warranty-types',
  PRIORITIES: '/master-data/priorities',
  APPROVAL_REQUEST_TYPES: '/master-data/approval-request-types',
  SETTINGS: '/settings',
  SETTINGS_COMPANY_PROFILE: '/settings/company-profile',
  SETTINGS_COMMUNICATION: '/settings/communication',
  SETTINGS_REPORT_STORAGE: '/settings/report-storage',
  SETTINGS_COMMUNICATION_EMAIL: '/settings/communication/email',
  SETTINGS_COMMUNICATION_TEMPLATES: '/settings/communication/templates',
  SETTINGS_COMMUNICATION_EMAIL_LOGS: '/settings/communication/email-logs',
  EQUIPMENT_INTAKE: '/equipment-intake',
  OPS_INTAKE_WORKSPACE: '/operations/intake-workspace',
  INSPECTIONS: '/inspections',
  INSPECTION_DETAIL: '/inspections/:id',
  /**
   * Request Thread Stage C — the canonical staff Request Thread workspace.
   *
   * Keyed by the MODIFICATION ROUND, because one customer request = one round =
   * one thread, and the round is the thread's identity. An inspection-keyed URL
   * cannot address a CLOSED round (only one round per job may be open), so this
   * is the only key that can name every thread.
   *
   * NOT to be confused with `REQUEST_CENTER` below, which is an unrelated SE/MSE
   * surface for ownership transfers and procedure authorizations. Different
   * entity, different data source, different audience — the names are adjacent
   * and the meanings are not.
   */
  REQUEST_THREAD: '/requests/:roundId',
  OPS_WORK_QUEUE: '/operations/work-queue',
  OPS_REVIEW_WORKSPACE: '/operations/review/:inspectionId',
  OPS_ASSIGNMENT_CENTER: '/operations/assignment-center',
  OPS_RECEIVING_LOTS: '/operations/receiving-lots',
  OPS_RECEIVING_LOT_DETAIL: '/operations/receiving-lots/:id',
  OPS_RECEIVING_LOT_MODEL_BATCH: '/operations/receiving-lots/:lotId/model-batches/:batchId',
  OPS_BULK_IMPORT_NEW: '/operations/bulk-import/new',
  OPS_BULK_IMPORT_DETAIL: '/operations/bulk-import/:id',
  AUTHORIZATION_CENTER: '/authorization-center',
  AUTHORIZATION_CASE_DETAIL: '/authorization-center/:id',
  MY_INSPECTIONS: '/my-inspections',
  JOB_POOL: '/job-pool',
  TEAM_QUEUE: '/team-queue',
  ORG_USERS: '/organization/users',
  ORG_INCOMPLETE_IDENTITY: '/organization/incomplete-identity',
  ORG_USER_DETAIL: '/organization/users/:id',
  ORG_DEPARTMENTS: '/organization/departments',
  ORG_ROLES: '/organization/roles',
  PROCEDURES: '/procedures',
  PROCEDURE_NEW: '/procedures/new',
  PROCEDURE_EDIT: '/procedures/:id/edit',
  PROCEDURE_VIEW: '/procedures/:id/view',
  PROCEDURE_VERSIONS: '/procedures/:id/versions',
  // Report Designer (B6.9e). Sited under /reports because the MSE surface is
  // "how the report template behaves" — a Reports concern, not a Settings one.
  // The SE composes an individual report from the inspection screen; these two
  // surfaces are deliberately never mixed.
  REPORT_TEMPLATES: '/reports/templates',
  REPORT_TEMPLATE_DESIGNER: '/reports/templates/:id',
  REPORT_TEMPLATE_VERSIONS: '/reports/templates/:id/versions',
  DASHBOARD_OWNER: '/dashboard/owner',
  DASHBOARD_ADMIN: '/dashboard/admin',
  DASHBOARD_OPERATIONS: '/dashboard/operations',
  DASHBOARD_COMMERCIAL: '/dashboard/commercial',
  DASHBOARD_ENGINEER: '/dashboard/engineer',
  DASHBOARD_MASTER_ENGINEER: '/dashboard/master-engineer',
  /**
   * The SE/MSE Request Center — ownership transfers and procedure
   * authorizations. Unrelated to `REQUEST_THREAD` above, which is the customer
   * modification thread. Neither route matches the other's path.
   */
  REQUEST_CENTER: '/request-center',
  PROFILE: '/profile',
} as const;

export type AppRoute = (typeof ROUTES)[keyof typeof ROUTES];
